/* eslint max-statements: ["error", {max: 30}] -- this applies for the entire file */
/* eslint max-lines-per-function: ["error", {max: 170}] -- this applies for the entire file */
import { useState } from "react";

import noop from "lodash/noop";

import { DateFormat } from "@/constants/DateFormat";
import { PatientDocumentValidationStatus } from "@/constants/Messages";
import type { FileMessage, GroupChannel } from "@/crossPlatform/Sendbird";
import { ReactionAddons, useBottomSheet } from "@/crossPlatform/Sendbird";
import useWebSafeChatPatientId from "@/hooks/useWebSafeChatPatientId";
import useAppMonitor from "@/services/AppMonitor";
import { useLazyGetPatientDocumentDownloadURLQuery } from "@/store/api/chat";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectInvalidAttachments,
  useAttachmentsDispatch,
} from "@/store/reducers/attachments";
import AttachmentPreviewModal from "@/ui/components/messages/documents/AttachmentPreviewModal";
import ReactionsBottomSheet from "@/ui/components/messages/fragments/ReactionsBottomSheet";
import { Avatar, Text } from "@/ui/core";
import BaseDocumentCard from "@/ui/core/BaseDocumentCard";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { formatDate } from "@/utils/date.util";
import {
  getMessageDirection,
  getPatientDocumentExternalIdFromFileName,
  removeUUIDFromFilename,
  sendbirdSenderToAvatarCard,
} from "@/utils/messages.util";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface GroupChannelFileMessageProps {
  channel: GroupChannel;
  message: FileMessage;
}

const GroupChannelFileMessage = ({
  channel,
  message,
}: GroupChannelFileMessageProps) => {
  const { externalId: appUserId } = useAppUserSelector();
  const selectedPatientId = useWebSafeChatPatientId(channel);

  const { openSheet } = useBottomSheet();

  const direction = getMessageDirection({
    appUserId,
    messageSender: message.sender,
  });

  const isIncoming = direction === "incoming";

  const displayName = removeUUIDFromFilename(message.name);
  const senderAvatar = sendbirdSenderToAvatarCard(message.sender);

  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  const { captureMessage } = useAppMonitor();

  const [query, { data, isLoading }] =
    useLazyGetPatientDocumentDownloadURLQuery();

  const baseTestID = joinTestIDs(
    "group-channel-file-message",
    `${message.messageId}`,
  );
  const documentURI = data?.attachmentDownloadUrl;
  const fileName = message.name;
  const documentExternalId = getPatientDocumentExternalIdFromFileName(fileName);

  const invalidAttachments = useAppSelector(selectInvalidAttachments);
  const isInvalid = invalidAttachments.includes(documentExternalId);

  const { addInvalidAttachment } = useAttachmentsDispatch();

  const formattedTime = formatDate({
    date: new Date(message.createdAt),
    // Matching Sendbird's time format
    format: DateFormat.time,
  }).toUpperCase();

  const handleDocumentPress = () => {
    if (!selectedPatientId) {
      captureMessage("selectedPatientId is missing from chat-channel-context");
      return;
    }

    void query({
      fileName,
      patientExternalId: selectedPatientId,
    })
      .unwrap()
      .then((response) => {
        const { Invalid } = PatientDocumentValidationStatus;
        if (response.fileValidationStatus === Invalid) {
          addInvalidAttachment(documentExternalId);
          return;
        }

        setIsDetailModalOpen(true);
      });
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
  };

  const bottomSheet = ReactionsBottomSheet({
    channel,
    message,
  });

  const handleLongPress = () => {
    openSheet(bottomSheet);
  };

  // on web, 80% stretches out the message too much, instead let it right-size
  const messageWidth = isWeb() ? undefined : "80%";

  return (
    <>
      <VStack gap={8} paddingHorizontal={16} paddingVertical={8}>
        {isIncoming && (
          <HStack alignItems="center" gap={8}>
            <Avatar
              imageUrl={senderAvatar.imageUrl}
              size="xxs"
              testID={baseTestID}
              userName={senderAvatar.userName}
              userType={senderAvatar.userType}
            />
            <Text
              testID={joinTestIDs(baseTestID, "sender")}
              variant="captionMedium"
            >
              {senderAvatar.userName}
            </Text>
          </HStack>
        )}
        <HStack
          alignItems="flex-end"
          flexDirection={isIncoming ? "row" : "row-reverse"}
          gap={8}
        >
          <Box width={messageWidth}>
            <BaseDocumentCard
              fileName={displayName}
              id={`${message.messageId}`}
              isInvalid={isInvalid}
              isLoading={isLoading}
              // on web, if onLongPress is defined, it always fires on any FileMessage press
              // on web, longPress is also not used for FileMessages anyhow (there's an emoji + button), so its a noop here
              onLongPress={isWeb() ? noop : handleLongPress}
              onPress={handleDocumentPress}
              testID={baseTestID}
            />
            <ReactionAddons.Message channel={channel} message={message} />
          </Box>
          <Text
            color={ColorToken.NeutralSecondary}
            testID={joinTestIDs(baseTestID, "time")}
            variant="caption"
          >
            {formattedTime}
          </Text>
        </HStack>
      </VStack>
      {documentURI && (
        <AttachmentPreviewModal
          isOpen={isDetailModalOpen}
          onClose={handleDetailModalClose}
          title={displayName}
          uri={documentURI}
        />
      )}
    </>
  );
};

export default GroupChannelFileMessage;
